import React, { useEffect, useState } from "react";
import { api, fmtNum, fmtPct, fmtMoney, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Brain, RefreshCw } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

function Metric({ label, value, positive, testid }) {
  return (
    <div className="surface rounded-sm p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`text-2xl font-mono mt-2 ${positive === true ? "text-pos" : positive === false ? "text-neg" : ""}`}>{value}</div>
    </div>
  );
}

export default function MLModel() {
  const [model, setModel] = useState(null);
  const [samples, setSamples] = useState(null);
  const [versions, setVersions] = useState([]);
  const [compare, setCompare] = useState(null);
  const [status, setStatus] = useState(null);
  const [training, setTraining] = useState(false);

  const load = async () => {
    const [m, s, v, c, st] = await Promise.all([
      api.get("/ml/model"),
      api.get("/ml/samples?limit=20"),
      api.get("/ml/versions"),
      api.get("/ml/strategy-compare"),
      api.get("/ml/status"),
    ]);
    setModel(m.data);
    setSamples(s.data);
    setVersions(v.data);
    setCompare(c.data);
    setStatus(st.data);
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const train = async () => {
    setTraining(true);
    try {
      const { data } = await api.post("/ml/train", {});
      toast.success(`Model ${data.version} trained — AUC ${data.val_auc ? data.val_auc.toFixed(3) : "—"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Training failed");
    } finally {
      setTraining(false);
    }
  };

  const importance = model?.feature_importance
    ? Object.entries(model.feature_importance)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
    : [];

  return (
    <div className="space-y-4" data-testid="ml-model-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">ML Model</h1>
          <p className="text-xs text-slate-500 uppercase tracking-[0.15em] mt-1">
            <Brain size={12} className="inline mr-1" />
            XGBoost binary + regression heads — trained on labelled paper trades
          </p>
        </div>
        <button
          data-testid="ml-train-btn"
          onClick={train}
          disabled={training}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs uppercase tracking-[0.15em] rounded-sm disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw size={14} className={training ? "animate-spin" : ""} /> {training ? "Training…" : "Train Now"}
        </button>
      </div>

      {status && (
        <div
          className="surface rounded-sm p-4 border-l-2 border-l-blue-500"
          data-testid="ml-engine-status"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300">
              ML Engine Status
            </div>
            <span
              data-testid="ml-collecting-badge"
              className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-[0.15em] ${
                status.collecting
                  ? "bg-emerald-950 text-emerald-300 border border-emerald-800"
                  : "bg-slate-900 text-slate-500 border border-slate-800"
              }`}
            >
              {status.collecting ? "● Collecting" : "○ Idle"}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Total samples
              </div>
              <div className="font-mono text-lg text-slate-100">
                {status.total_samples ?? 0}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Last 24h
              </div>
              <div className="font-mono text-lg text-slate-100">
                {status.samples_last_24h ?? 0}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Last 7d
              </div>
              <div className="font-mono text-lg text-slate-100">
                {status.samples_last_7d ?? 0}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Labeled (closed)
              </div>
              <div className="font-mono text-lg text-slate-100">
                {status.labeled_samples ?? 0}{" "}
                <span className="text-slate-500 text-xs">
                  / {status.min_samples_for_training} to train
                </span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Signals w/ ML
              </div>
              <div className="font-mono text-lg text-slate-100">
                {status.signals_with_ml_prediction ?? 0}
                <span className="text-slate-500 text-xs">
                  {" / "}{status.signals_total ?? 0}
                </span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                Model
              </div>
              <div className="font-mono text-lg">
                {status.model_trained ? (
                  <span className="text-emerald-400">Trained</span>
                ) : (
                  <span className="text-amber-400">Not trained</span>
                )}
              </div>
            </div>
          </div>
          {status.latest_sample && status.latest_sample.timestamp && (
            <div className="mt-3 text-[11px] font-mono text-slate-400 border-t border-slate-800 pt-2">
              Last sample:{" "}
              <span className="text-blue-300">
                {String(status.latest_sample.timestamp).slice(0, 19).replace("T", " ")}
              </span>
              {status.latest_sample.instrument && (
                <> · {status.latest_sample.instrument}</>
              )}
              {status.latest_sample.ml_probability != null && (
                <>
                  {" "}· prob{" "}
                  <span className="text-blue-300">
                    {(status.latest_sample.ml_probability * 100).toFixed(1)}%
                  </span>
                </>
              )}
            </div>
          )}
          {!status.collecting && (
            <div className="mt-3 text-[11px] text-amber-300 leading-relaxed">
              No samples yet. The ML layer records one sample per signal
              evaluation during market hours. Wait for the next 5-min bar
              (or run a demo session) to see it activate.
            </div>
          )}
          {status.collecting && !status.can_train && (
            <div className="mt-3 text-[11px] text-slate-400 leading-relaxed">
              Not enough labeled samples yet — each closed paper trade adds
              one label. Once you have {status.min_samples_for_training}+
              closed trades, the <span className="text-blue-300">Train Now</span>{" "}
              button will produce a real predictor.
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Total Samples" value={samples?.total ?? 0} testid="metric-samples-total" />
        <Metric label="Labeled" value={samples?.labeled ?? 0} testid="metric-samples-labeled" />
        <Metric label="Wins" value={samples?.positive ?? 0} testid="metric-samples-positive" />
        <Metric
          label="Model Status"
          value={model?.trained ? model.version : "Cold"}
          positive={model?.trained}
          testid="metric-model-status"
        />
      </div>

      {!model?.trained && (
        <div className="surface rounded-sm p-6 border-l-2 border-l-amber-500">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-2">Cold Start</div>
          <p className="text-sm text-slate-300 leading-relaxed">
            {model?.message || "No model trained yet."} As paper trades close, samples accumulate automatically. Once you have at least 30 labeled trades (with both wins and losses), click <span className="text-blue-400 font-mono">Train Now</span>.
          </p>
        </div>
      )}

      {model?.trained && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Val AUC" value={fmtNum(model.val_auc, 3)} positive={model.val_auc > 0.6} testid="metric-auc" />
            <Metric label="Val Accuracy" value={fmtPct(model.val_accuracy)} testid="metric-accuracy" />
            <Metric label="Precision" value={fmtPct(model.val_precision)} testid="metric-precision" />
            <Metric label="Recall" value={fmtPct(model.val_recall)} testid="metric-recall" />
          </div>

          <div className="surface rounded-sm p-4" data-testid="feature-importance">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Feature Importance</div>
            <div style={{ height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={importance} layout="vertical" margin={{ top: 10, right: 20, left: 120, bottom: 10 }}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="2 2" />
                  <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11, fontFamily: "JetBrains Mono" }} width={120} />
                  <Tooltip contentStyle={{ background: "#131c2d", border: "1px solid #1e293b", fontSize: 11 }} />
                  <Bar dataKey="value" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {compare && (
        <div className="surface rounded-sm p-4" data-testid="strategy-compare">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">Strategy Comparison</div>
          <div className="grid grid-cols-2 gap-4">
            {[
              ["Deterministic (v1)", compare.deterministic],
              ["ML-Recommended", compare.ml_recommended],
            ].map(([label, s]) => (
              <div key={label} className="border border-slate-800 rounded-sm p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-3">{label}</div>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div><span className="text-slate-500">Trades: </span>{s.total_trades}</div>
                  <div><span className="text-slate-500">Wins: </span>{s.wins}</div>
                  <div><span className="text-slate-500">Losses: </span>{s.losses}</div>
                  <div><span className="text-slate-500">Win rate: </span>{fmtPct(s.win_rate)}</div>
                  <div className="col-span-2">
                    <span className="text-slate-500">Net P&L: </span>
                    <span className={s.net_pnl >= 0 ? "text-pos" : "text-neg"}>{fmtMoney(s.net_pnl)}</span>
                  </div>
                  <div><span className="text-slate-500">PF: </span>{s.profit_factor ? fmtNum(s.profit_factor) : "—"}</div>
                  <div><span className="text-slate-500">Exp: </span>{fmtMoney(s.expectancy)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {versions?.length > 0 && (
        <div className="surface rounded-sm overflow-x-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 px-4 pt-4">Version History</div>
          <table className="dt mt-2">
            <thead>
              <tr>
                <th>Version</th>
                <th>Trained</th>
                <th className="num">Samples</th>
                <th className="num">Positives</th>
                <th className="num">Val AUC</th>
                <th className="num">Accuracy</th>
                <th className="num">Precision</th>
                <th className="num">Recall</th>
                <th className="num">Threshold</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.version} data-testid="ml-version-row">
                  <td className="font-mono text-blue-300">{v.version}</td>
                  <td className="font-mono text-slate-400">{fmtDateTime(v.trained_at)}</td>
                  <td className="num font-mono">{v.n_samples}</td>
                  <td className="num font-mono">{v.n_positive}</td>
                  <td className="num font-mono">{v.val_auc ? fmtNum(v.val_auc, 3) : "—"}</td>
                  <td className="num font-mono">{fmtPct(v.val_accuracy)}</td>
                  <td className="num font-mono">{fmtPct(v.val_precision)}</td>
                  <td className="num font-mono">{fmtPct(v.val_recall)}</td>
                  <td className="num font-mono">{fmtNum(v.prob_threshold)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
